import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import {
  Button,
  Input,
  message,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Table,
  TimePicker,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { dayjsLocale } from '../i18n/locale';

const { Text } = Typography;

interface SessionDto {
  id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  location?: string;
  type?: string;
}

interface Props {
  eventId: string;
  eventStartDate: string;
  eventEndDate: string;
  sessions: SessionDto[];
  onChange: () => void;
}

// A kulcsok szandekosan literalok: a t() tipusa igy ellenorizni tudja oket,
// egy `eventProgram.types.${value}` sablonnal viszont nem tudna.
const SESSION_TYPES = [
  { value: 'KEYNOTE', labelKey: 'eventProgram.types.keynote' },
  { value: 'PRESENTATION', labelKey: 'eventProgram.types.presentation' },
  { value: 'WORKSHOP', labelKey: 'eventProgram.types.workshop' },
  { value: 'PANEL', labelKey: 'eventProgram.types.panel' },
  { value: 'NETWORKING', labelKey: 'eventProgram.types.networking' },
  { value: 'BREAK', labelKey: 'eventProgram.types.break' },
] as const;

/**
 * Egy sor a tábla state-jében. A szerkesztés in-place: a felhasználó
 * minden cellát mindig módosíthat, és a sor végén lévő Mentés gomb küldi
 * a backendet. A `dirty` flag jelzi hogy van mentésre váró változás.
 */
interface DraftRow {
  key: string;
  id?: string;
  isNew?: boolean;
  dirty?: boolean;
  saving?: boolean;
  title: string;
  type: string;
  // Csak idő-rész; a dátum a kiválasztott napból jön.
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  location: string;
  description: string;
}

function tempKey(): string {
  return `new-${Math.random().toString(36).slice(2, 9)}`;
}

function toDraft(s: SessionDto): DraftRow {
  return {
    key: s.id,
    id: s.id,
    title: s.title,
    type: s.type ?? 'PRESENTATION',
    startTime: dayjs(s.startTime),
    endTime: dayjs(s.endTime),
    location: s.location ?? '',
    description: s.description ?? '',
  };
}

export default function EventProgramEditor({
  eventId,
  eventStartDate,
  eventEndDate,
  sessions,
  onChange,
}: Props) {
  const { t, i18n } = useTranslation();

  const sessionTypeOptions = useMemo(
    () => SESSION_TYPES.map(({ value, labelKey }) => ({ value, label: t(labelKey) })),
    [t]
  );

  // Multi-day esemény napjai (a start és end napok közötti lista)
  const days = useMemo<Dayjs[]>(() => {
    const out: Dayjs[] = [];
    let cursor = dayjs(eventStartDate).startOf('day');
    const last = dayjs(eventEndDate).startOf('day');
    // safety: ha startDate > endDate (rossz adat), legalább 1 napot adjunk
    if (last.isBefore(cursor)) return [cursor];
    while (cursor.isBefore(last) || cursor.isSame(last)) {
      out.push(cursor);
      cursor = cursor.add(1, 'day');
    }
    return out;
  }, [eventStartDate, eventEndDate]);

  const [selectedDay, setSelectedDay] = useState<Dayjs>(days[0]);

  // A backendtől kapott session-öket leszűrjük az aktuális napra,
  // majd a helyi state-be másoljuk. Új sor hozzáadása és élő szerkesztés
  // is itt megy. A backend-ből frissítés (`onChange`) után újratölt.
  const [rows, setRows] = useState<DraftRow[]>([]);

  useEffect(() => {
    setRows(
      sessions
        .filter((s) => dayjs(s.startTime).startOf('day').isSame(selectedDay, 'day'))
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
        .map(toDraft)
    );
  }, [sessions, selectedDay]);

  const updateRow = (key: string, patch: Partial<DraftRow>) => {
    setRows((current) => current.map((r) => (r.key === key ? { ...r, ...patch, dirty: true } : r)));
  };

  const addRow = () => {
    const last = rows[rows.length - 1];
    const defaultStart = last?.endTime ?? selectedDay.clone().hour(9).minute(0);
    const defaultEnd = defaultStart.clone().add(30, 'minute');
    setRows((current) => [
      ...current,
      {
        key: tempKey(),
        isNew: true,
        dirty: true,
        title: '',
        type: 'PRESENTATION',
        startTime: defaultStart,
        endTime: defaultEnd,
        location: '',
        description: '',
      },
    ]);
  };

  const combineDateAndTime = (time: Dayjs | null): Dayjs | null => {
    if (!time) return null;
    return selectedDay.clone().hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
  };

  const saveRow = async (row: DraftRow) => {
    if (!row.title?.trim()) {
      message.error(t('eventProgram.titleRequired'));
      return;
    }
    if (!(row.startTime && row.endTime)) {
      message.error(t('eventProgram.timesRequired'));
      return;
    }
    if (!row.endTime.isAfter(row.startTime)) {
      message.error(t('eventProgram.endAfterStart'));
      return;
    }

    const payload = {
      title: row.title.trim(),
      description: row.description?.trim() || undefined,
      startTime: combineDateAndTime(row.startTime)!.toISOString(),
      endTime: combineDateAndTime(row.endTime)!.toISOString(),
      location: row.location?.trim() || undefined,
      type: row.type,
    };

    setRows((current) => current.map((r) => (r.key === row.key ? { ...r, saving: true } : r)));

    try {
      if (row.isNew) {
        await api.post('/sessions', { ...payload, eventId });
        message.success(t('eventProgram.created'));
      } else if (row.id) {
        await api.patch(`/sessions/${row.id}`, payload);
        message.success(t('eventProgram.updated'));
      }
      onChange();
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('eventProgram.saveFailed'));
      setRows((current) => current.map((r) => (r.key === row.key ? { ...r, saving: false } : r)));
    }
  };

  const deleteRow = async (row: DraftRow) => {
    if (row.isNew) {
      // Még nincs a backenden — egyszerűen vegyük ki a state-ből
      setRows((current) => current.filter((r) => r.key !== row.key));
      return;
    }
    if (!row.id) return;
    try {
      await api.delete(`/sessions/${row.id}`);
      message.success(t('eventProgram.deleted'));
      onChange();
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('eventProgram.deleteFailed'));
    }
  };

  const columns = [
    {
      title: t('eventProgram.columnStart'),
      width: 110,
      key: 'startTime',
      render: (_: unknown, row: DraftRow) => (
        <TimePicker
          value={row.startTime}
          onChange={(v) => updateRow(row.key, { startTime: v })}
          format="HH:mm"
          minuteStep={5}
          allowClear={false}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('eventProgram.columnEnd'),
      width: 110,
      key: 'endTime',
      render: (_: unknown, row: DraftRow) => (
        <TimePicker
          value={row.endTime}
          onChange={(v) => updateRow(row.key, { endTime: v })}
          format="HH:mm"
          minuteStep={5}
          allowClear={false}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('eventProgram.columnTitle'),
      key: 'title',
      render: (_: unknown, row: DraftRow) => (
        <Input
          value={row.title}
          placeholder={t('eventProgram.titlePlaceholder')}
          onChange={(e) => updateRow(row.key, { title: e.target.value })}
        />
      ),
    },
    {
      title: t('eventProgram.columnType'),
      width: 160,
      key: 'type',
      render: (_: unknown, row: DraftRow) => (
        <Select
          value={row.type}
          onChange={(v) => updateRow(row.key, { type: v })}
          options={sessionTypeOptions}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('eventProgram.columnLocation'),
      width: 180,
      key: 'location',
      render: (_: unknown, row: DraftRow) => (
        <Input
          value={row.location}
          placeholder={t('eventProgram.locationPlaceholder')}
          onChange={(e) => updateRow(row.key, { location: e.target.value })}
        />
      ),
    },
    {
      title: '',
      width: 130,
      key: 'actions',
      render: (_: unknown, row: DraftRow) => (
        <Space size={4}>
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            onClick={() => saveRow(row)}
            disabled={!row.dirty}
            loading={row.saving}
          >
            {row.isNew ? t('eventProgram.create') : t('eventProgram.save')}
          </Button>
          <Popconfirm
            title={t('eventProgram.deleteConfirm')}
            okText={t('eventProgram.deleteYes')}
            cancelText={t('eventProgram.deleteCancel')}
            onConfirm={() => deleteRow(row)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {days.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ marginRight: 12 }}>
            {t('eventProgram.day')}
          </Text>
          <Segmented
            value={selectedDay.format('YYYY-MM-DD')}
            onChange={(v) => setSelectedDay(dayjs(String(v)))}
            options={days.map((d) => ({
              label: d.locale(dayjsLocale(i18n.language)).format('MMM D. (ddd)'),
              value: d.format('YYYY-MM-DD'),
            }))}
          />
        </div>
      )}

      <Table
        rowKey="key"
        size="middle"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: t('eventProgram.empty') }}
        footer={() => (
          <Button type="dashed" icon={<PlusOutlined />} onClick={addRow} block>
            {t('eventProgram.add')}
          </Button>
        )}
      />
    </div>
  );
}
